/**
 * Embed palettes for Prediction Market.
 *
 * Use via URL:  ?theme=pvw  (dark)  |  ?theme=pvw-light
 * Or postMessage: { type: 'SET_THEME', theme: 'pvw-light' }
 *
 * Light mode is only available with the `-light` suffix. Every palette
 * ships light + dark token sets.
 */

const RADII = {
  'pm-radius': '8px',
  'pm-radius-sm': '6px',
  'pm-radius-lg': '10px'
}

/** Shared semantic statuses tinted lightly toward the accent family */
const statusLight = {
  'color-success': '22 128 74',
  'color-success-soft': '214 240 224',
  'color-success-contrast': '15 92 52',
  'color-danger': '196 48 48',
  'color-danger-soft': '252 226 226',
  'color-danger-contrast': '140 28 28',
  'color-warning': '180 122 16',
  'color-warning-soft': '250 236 198',
  'color-warning-contrast': '120 78 8'
}

const statusDark = {
  'color-success': '46 196 118',
  'color-success-soft': '12 58 38',
  'color-success-contrast': '167 243 208',
  'color-danger': '232 72 72',
  'color-danger-soft': '72 24 24',
  'color-danger-contrast': '254 202 202',
  'color-warning': '232 176 48',
  'color-warning-soft': '64 44 12',
  'color-warning-contrast': '254 236 180'
}

const pack = (light, dark) => ({
  light: { ...RADII, ...statusLight, ...light },
  dark: { ...RADII, ...statusDark, ...dark }
})

export const DEFAULT_PALETTE = 'pvw'
export const DEFAULT_MODE = 'dark'

/**
 * @typedef {{ id: string, label: string, light: Record<string, string>, dark: Record<string, string> }} ThemePalette
 * @type {Record<string, ThemePalette>}
 */
export const THEMES = {
  /** Casino default — felt green + gold */
  felt: {
    id: 'felt',
    label: 'Felt Gold',
    ...pack(
      {
        'color-surface': '236 242 238',
        'color-surface-raised': '250 252 250',
        'color-surface-muted': '226 234 228',
        'color-surface-overlay': '244 248 245',
        'color-ink': '18 32 26',
        'color-ink-soft': '32 48 40',
        'color-ink-muted': '74 96 84',
        'color-ink-faint': '110 130 118',
        'color-ink-inverse': '255 255 255',
        'color-line': '206 220 210',
        'color-line-strong': '184 202 190',
        'color-border-card': '206 220 210',
        'color-accent': '176 134 28',
        'color-accent-soft': '245 234 196',
        'color-accent-contrast': '255 255 255',
        'color-info': '36 110 90',
        'color-info-soft': '214 236 226',
        'color-info-contrast': '20 78 62',
        'color-shadow': '18 40 28',
        'pm-gold': '201 162 39',
        'pm-felt': '22 90 58'
      },
      {
        'color-surface': '8 18 14',
        'color-surface-raised': '14 28 22',
        'color-surface-muted': '20 38 30',
        'color-surface-overlay': '18 34 27',
        'color-ink': '236 244 238',
        'color-ink-soft': '214 228 218',
        'color-ink-muted': '148 172 158',
        'color-ink-faint': '108 132 118',
        'color-ink-inverse': '8 18 14',
        'color-line': '32 54 44',
        'color-line-strong': '46 74 60',
        'color-border-card': '36 58 48',
        'color-accent': '212 168 42',
        'color-accent-soft': '58 48 18',
        'color-accent-contrast': '12 18 14',
        'color-info': '72 168 140',
        'color-info-soft': '18 52 42',
        'color-info-contrast': '180 236 216',
        'color-shadow': '0 0 0',
        'pm-gold': '212 168 42',
        'pm-felt': '14 72 48'
      }
    )
  },

  /** purple / violet / white */
  pvw: {
    id: 'pvw',
    label: 'Purple Violet White',
    ...pack(
      {
        'color-surface': '246 242 252',
        'color-surface-raised': '255 255 255',
        'color-surface-muted': '236 228 248',
        'color-surface-overlay': '250 246 255',
        'color-ink': '36 22 58',
        'color-ink-soft': '54 36 82',
        'color-ink-muted': '102 82 138',
        'color-ink-faint': '136 118 168',
        'color-ink-inverse': '255 255 255',
        'color-line': '222 210 238',
        'color-line-strong': '198 180 226',
        'color-border-card': '222 210 238',
        'color-accent': '124 58 200',
        'color-accent-soft': '236 222 255',
        'color-accent-contrast': '255 255 255',
        'color-info': '98 72 180',
        'color-info-soft': '228 220 252',
        'color-info-contrast': '58 40 130',
        'color-shadow': '48 28 80',
        'pm-gold': '168 110 240',
        'pm-felt': '88 42 150'
      },
      {
        'color-surface': '14 10 24',
        'color-surface-raised': '26 18 42',
        'color-surface-muted': '36 26 56',
        'color-surface-overlay': '32 22 50',
        'color-ink': '242 236 255',
        'color-ink-soft': '220 210 244',
        'color-ink-muted': '160 146 198',
        'color-ink-faint': '120 108 158',
        'color-ink-inverse': '14 10 24',
        'color-line': '48 36 78',
        'color-line-strong': '68 50 108',
        'color-border-card': '54 40 88',
        'color-accent': '186 140 255',
        'color-accent-soft': '58 36 98',
        'color-accent-contrast': '18 10 32',
        'color-info': '150 128 230',
        'color-info-soft': '36 28 72',
        'color-info-contrast': '210 200 255',
        'color-shadow': '0 0 0',
        'pm-gold': '186 140 255',
        'pm-felt': '72 40 140'
      }
    )
  },

  /** navy / blue / white */
  nbw: {
    id: 'nbw',
    label: 'Navy Blue White',
    ...pack(
      {
        'color-surface': '240 244 250',
        'color-surface-raised': '255 255 255',
        'color-surface-muted': '226 234 246',
        'color-surface-overlay': '246 248 252',
        'color-ink': '14 28 52',
        'color-ink-soft': '28 44 78',
        'color-ink-muted': '72 96 132',
        'color-ink-faint': '110 128 158',
        'color-ink-inverse': '255 255 255',
        'color-line': '206 218 236',
        'color-line-strong': '178 196 224',
        'color-border-card': '206 218 236',
        'color-accent': '36 98 210',
        'color-accent-soft': '214 230 255',
        'color-accent-contrast': '255 255 255',
        'color-info': '40 110 180',
        'color-info-soft': '214 234 250',
        'color-info-contrast': '20 72 128',
        'color-shadow': '20 36 64',
        'pm-gold': '64 140 236',
        'pm-felt': '28 64 140'
      },
      {
        'color-surface': '8 14 26',
        'color-surface-raised': '14 24 42',
        'color-surface-muted': '22 36 58',
        'color-surface-overlay': '18 30 50',
        'color-ink': '232 240 252',
        'color-ink-soft': '204 218 240',
        'color-ink-muted': '140 160 196',
        'color-ink-faint': '100 120 156',
        'color-ink-inverse': '8 14 26',
        'color-line': '32 48 78',
        'color-line-strong': '46 68 104',
        'color-border-card': '36 54 86',
        'color-accent': '96 168 255',
        'color-accent-soft': '24 48 92',
        'color-accent-contrast': '8 16 32',
        'color-info': '88 158 220',
        'color-info-soft': '18 40 72',
        'color-info-contrast': '186 224 255',
        'color-shadow': '0 0 0',
        'pm-gold': '96 168 255',
        'pm-felt': '32 72 150'
      }
    )
  },

  /** crimson / red / black */
  crb: {
    id: 'crb',
    label: 'Crimson Red Black',
    ...pack(
      {
        'color-surface': '250 242 242',
        'color-surface-raised': '255 255 255',
        'color-surface-muted': '244 228 228',
        'color-surface-overlay': '252 246 246',
        'color-ink': '42 16 16',
        'color-ink-soft': '68 28 28',
        'color-ink-muted': '128 72 72',
        'color-ink-faint': '158 108 108',
        'color-ink-inverse': '255 255 255',
        'color-line': '236 212 212',
        'color-line-strong': '220 184 184',
        'color-border-card': '236 212 212',
        'color-accent': '188 36 48',
        'color-accent-soft': '252 220 222',
        'color-accent-contrast': '255 255 255',
        'color-info': '160 60 70',
        'color-info-soft': '250 228 230',
        'color-info-contrast': '120 36 42',
        'color-shadow': '56 20 20',
        'pm-gold': '214 72 72',
        'pm-felt': '120 28 36'
      },
      {
        'color-surface': '14 8 8',
        'color-surface-raised': '28 14 14',
        'color-surface-muted': '42 22 22',
        'color-surface-overlay': '34 18 18',
        'color-ink': '252 236 236',
        'color-ink-soft': '236 210 210',
        'color-ink-muted': '190 140 140',
        'color-ink-faint': '148 110 110',
        'color-ink-inverse': '14 8 8',
        'color-line': '58 28 28',
        'color-line-strong': '86 40 40',
        'color-border-card': '66 32 32',
        'color-accent': '240 88 96',
        'color-accent-soft': '72 24 28',
        'color-accent-contrast': '20 8 8',
        'color-info': '220 120 120',
        'color-info-soft': '52 22 24',
        'color-info-contrast': '255 210 210',
        'color-shadow': '0 0 0',
        'pm-gold': '240 88 96',
        'pm-felt': '120 32 40'
      }
    )
  },

  /** teal / emerald / white */
  tew: {
    id: 'tew',
    label: 'Teal Emerald White',
    ...pack(
      {
        'color-surface': '236 248 246',
        'color-surface-raised': '255 255 255',
        'color-surface-muted': '220 240 236',
        'color-surface-overlay': '244 252 250',
        'color-ink': '12 40 36',
        'color-ink-soft': '24 58 52',
        'color-ink-muted': '56 110 100',
        'color-ink-faint': '96 136 128',
        'color-ink-inverse': '255 255 255',
        'color-line': '196 228 220',
        'color-line-strong': '164 208 196',
        'color-border-card': '196 228 220',
        'color-accent': '16 148 128',
        'color-accent-soft': '204 244 234',
        'color-accent-contrast': '255 255 255',
        'color-info': '24 132 120',
        'color-info-soft': '210 240 234',
        'color-info-contrast': '12 88 78',
        'color-shadow': '16 48 42',
        'pm-gold': '32 180 150',
        'pm-felt': '12 110 92'
      },
      {
        'color-surface': '6 18 16',
        'color-surface-raised': '12 30 28',
        'color-surface-muted': '18 42 38',
        'color-surface-overlay': '14 36 32',
        'color-ink': '228 248 244',
        'color-ink-soft': '200 232 224',
        'color-ink-muted': '132 180 168',
        'color-ink-faint': '96 140 130',
        'color-ink-inverse': '6 18 16',
        'color-line': '28 56 50',
        'color-line-strong': '40 78 70',
        'color-border-card': '32 62 56',
        'color-accent': '52 212 180',
        'color-accent-soft': '16 58 50',
        'color-accent-contrast': '6 18 16',
        'color-info': '64 188 168',
        'color-info-soft': '14 48 44',
        'color-info-contrast': '180 244 232',
        'color-shadow': '0 0 0',
        'pm-gold': '52 212 180',
        'pm-felt': '18 100 88'
      }
    )
  },

  /** amber / bronze / black */
  abk: {
    id: 'abk',
    label: 'Amber Bronze Black',
    ...pack(
      {
        'color-surface': '250 244 234',
        'color-surface-raised': '255 252 246',
        'color-surface-muted': '240 228 208',
        'color-surface-overlay': '252 248 240',
        'color-ink': '40 28 12',
        'color-ink-soft': '64 46 18',
        'color-ink-muted': '120 92 48',
        'color-ink-faint': '150 122 78',
        'color-ink-inverse': '255 255 255',
        'color-line': '230 214 186',
        'color-line-strong': '210 188 150',
        'color-border-card': '230 214 186',
        'color-accent': '180 118 20',
        'color-accent-soft': '252 230 186',
        'color-accent-contrast': '255 255 255',
        'color-info': '150 100 30',
        'color-info-soft': '248 232 198',
        'color-info-contrast': '100 64 12',
        'color-shadow': '48 32 12',
        'pm-gold': '212 148 32',
        'pm-felt': '120 72 16'
      },
      {
        'color-surface': '14 10 6',
        'color-surface-raised': '26 20 12',
        'color-surface-muted': '40 30 16',
        'color-surface-overlay': '32 24 14',
        'color-ink': '252 240 220',
        'color-ink-soft': '236 216 180',
        'color-ink-muted': '186 156 100',
        'color-ink-faint': '148 122 78',
        'color-ink-inverse': '14 10 6',
        'color-line': '56 42 22',
        'color-line-strong': '84 62 30',
        'color-border-card': '64 48 24',
        'color-accent': '240 176 48',
        'color-accent-soft': '64 44 12',
        'color-accent-contrast': '18 12 4',
        'color-info': '220 168 64',
        'color-info-soft': '48 34 12',
        'color-info-contrast': '255 228 170',
        'color-shadow': '0 0 0',
        'pm-gold': '240 176 48',
        'pm-felt': '120 78 20'
      }
    )
  },

  /** ice / cyan / blue */
  icb: {
    id: 'icb',
    label: 'Ice Cyan Blue',
    ...pack(
      {
        'color-surface': '238 248 252',
        'color-surface-raised': '255 255 255',
        'color-surface-muted': '220 240 248',
        'color-surface-overlay': '244 252 255',
        'color-ink': '12 36 52',
        'color-ink-soft': '24 54 74',
        'color-ink-muted': '64 108 132',
        'color-ink-faint': '100 136 156',
        'color-ink-inverse': '255 255 255',
        'color-line': '198 228 240',
        'color-line-strong': '164 210 228',
        'color-border-card': '198 228 240',
        'color-accent': '8 148 180',
        'color-accent-soft': '204 240 252',
        'color-accent-contrast': '255 255 255',
        'color-info': '24 132 176',
        'color-info-soft': '210 236 250',
        'color-info-contrast': '12 88 120',
        'color-shadow': '16 44 60',
        'pm-gold': '32 180 220',
        'pm-felt': '16 110 150'
      },
      {
        'color-surface': '6 16 22',
        'color-surface-raised': '12 28 36',
        'color-surface-muted': '18 40 52',
        'color-surface-overlay': '14 34 44',
        'color-ink': '228 246 252',
        'color-ink-soft': '196 230 242',
        'color-ink-muted': '128 176 196',
        'color-ink-faint': '92 140 160',
        'color-ink-inverse': '6 16 22',
        'color-line': '28 56 70',
        'color-line-strong': '40 78 96',
        'color-border-card': '32 62 78',
        'color-accent': '64 210 240',
        'color-accent-soft': '14 52 68',
        'color-accent-contrast': '6 16 22',
        'color-info': '72 184 220',
        'color-info-soft': '12 48 62',
        'color-info-contrast': '186 238 255',
        'color-shadow': '0 0 0',
        'pm-gold': '64 210 240',
        'pm-felt': '20 100 130'
      }
    )
  },

  /** rose / gold / white */
  rgw: {
    id: 'rgw',
    label: 'Rose Gold White',
    ...pack(
      {
        'color-surface': '252 242 244',
        'color-surface-raised': '255 255 255',
        'color-surface-muted': '246 228 232',
        'color-surface-overlay': '255 248 250',
        'color-ink': '52 24 34',
        'color-ink-soft': '78 36 50',
        'color-ink-muted': '140 86 102',
        'color-ink-faint': '168 120 134',
        'color-ink-inverse': '255 255 255',
        'color-line': '238 214 220',
        'color-line-strong': '224 186 196',
        'color-border-card': '238 214 220',
        'color-accent': '186 96 118',
        'color-accent-soft': '252 224 232',
        'color-accent-contrast': '255 255 255',
        'color-info': '160 90 110',
        'color-info-soft': '250 228 234',
        'color-info-contrast': '120 54 72',
        'color-shadow': '56 28 36',
        'pm-gold': '210 150 90',
        'pm-felt': '148 64 88'
      },
      {
        'color-surface': '16 10 14',
        'color-surface-raised': '30 18 24',
        'color-surface-muted': '44 28 36',
        'color-surface-overlay': '36 22 30',
        'color-ink': '252 236 242',
        'color-ink-soft': '240 214 224',
        'color-ink-muted': '196 148 164',
        'color-ink-faint': '156 116 132',
        'color-ink-inverse': '16 10 14',
        'color-line': '62 36 46',
        'color-line-strong': '90 52 66',
        'color-border-card': '70 42 54',
        'color-accent': '236 148 168',
        'color-accent-soft': '72 32 46',
        'color-accent-contrast': '22 10 16',
        'color-info': '220 140 160',
        'color-info-soft': '52 26 36',
        'color-info-contrast': '255 214 224',
        'color-shadow': '0 0 0',
        'pm-gold': '230 176 112',
        'pm-felt': '140 56 84'
      }
    )
  },

  /** slate / graphite / gray */
  slg: {
    id: 'slg',
    label: 'Slate Graphite Gray',
    ...pack(
      {
        'color-surface': '242 244 246',
        'color-surface-raised': '255 255 255',
        'color-surface-muted': '228 232 236',
        'color-surface-overlay': '248 249 250',
        'color-ink': '22 26 32',
        'color-ink-soft': '40 46 56',
        'color-ink-muted': '90 100 114',
        'color-ink-faint': '124 134 148',
        'color-ink-inverse': '255 255 255',
        'color-line': '210 216 224',
        'color-line-strong': '184 194 206',
        'color-border-card': '210 216 224',
        'color-accent': '72 88 112',
        'color-accent-soft': '224 230 240',
        'color-accent-contrast': '255 255 255',
        'color-info': '70 96 130',
        'color-info-soft': '222 232 244',
        'color-info-contrast': '40 58 88',
        'color-shadow': '24 28 36',
        'pm-gold': '120 140 168',
        'pm-felt': '56 72 96'
      },
      {
        'color-surface': '10 12 16',
        'color-surface-raised': '18 22 28',
        'color-surface-muted': '28 34 42',
        'color-surface-overlay': '22 26 34',
        'color-ink': '236 240 244',
        'color-ink-soft': '210 216 224',
        'color-ink-muted': '148 158 172',
        'color-ink-faint': '110 120 136',
        'color-ink-inverse': '10 12 16',
        'color-line': '40 48 60',
        'color-line-strong': '58 70 86',
        'color-border-card': '46 54 68',
        'color-accent': '156 176 204',
        'color-accent-soft': '36 44 58',
        'color-accent-contrast': '12 14 18',
        'color-info': '140 168 210',
        'color-info-soft': '28 36 52',
        'color-info-contrast': '210 224 244',
        'color-shadow': '0 0 0',
        'pm-gold': '156 176 204',
        'pm-felt': '64 80 108'
      }
    )
  },

  /** orange / ember / black */
  oeb: {
    id: 'oeb',
    label: 'Orange Ember Black',
    ...pack(
      {
        'color-surface': '252 244 236',
        'color-surface-raised': '255 252 248',
        'color-surface-muted': '246 230 214',
        'color-surface-overlay': '255 248 242',
        'color-ink': '44 24 10',
        'color-ink-soft': '72 40 16',
        'color-ink-muted': '140 88 48',
        'color-ink-faint': '168 120 80',
        'color-ink-inverse': '255 255 255',
        'color-line': '236 214 190',
        'color-line-strong': '220 188 152',
        'color-border-card': '236 214 190',
        'color-accent': '212 96 24',
        'color-accent-soft': '255 228 198',
        'color-accent-contrast': '255 255 255',
        'color-info': '180 90 30',
        'color-info-soft': '252 230 206',
        'color-info-contrast': '120 52 12',
        'color-shadow': '52 28 10',
        'pm-gold': '240 140 40',
        'pm-felt': '160 64 16'
      },
      {
        'color-surface': '14 8 4',
        'color-surface-raised': '28 16 8',
        'color-surface-muted': '42 26 12',
        'color-surface-overlay': '34 20 10',
        'color-ink': '255 240 228',
        'color-ink-soft': '244 214 186',
        'color-ink-muted': '200 148 100',
        'color-ink-faint': '160 116 78',
        'color-ink-inverse': '14 8 4',
        'color-line': '60 34 16',
        'color-line-strong': '90 50 22',
        'color-border-card': '70 40 18',
        'color-accent': '255 148 56',
        'color-accent-soft': '72 36 10',
        'color-accent-contrast': '20 10 4',
        'color-info': '240 150 70',
        'color-info-soft': '52 28 10',
        'color-info-contrast': '255 220 180',
        'color-shadow': '0 0 0',
        'pm-gold': '255 148 56',
        'pm-felt': '160 72 20'
      }
    )
  },

  /** pink / indigo / violet */
  piv: {
    id: 'piv',
    label: 'Pink Indigo Violet',
    ...pack(
      {
        'color-surface': '250 240 250',
        'color-surface-raised': '255 255 255',
        'color-surface-muted': '242 226 246',
        'color-surface-overlay': '252 246 254',
        'color-ink': '40 20 56',
        'color-ink-soft': '62 34 84',
        'color-ink-muted': '120 78 148',
        'color-ink-faint': '150 112 176',
        'color-ink-inverse': '255 255 255',
        'color-line': '232 210 240',
        'color-line-strong': '214 180 230',
        'color-border-card': '232 210 240',
        'color-accent': '190 56 170',
        'color-accent-soft': '250 220 246',
        'color-accent-contrast': '255 255 255',
        'color-info': '110 72 190',
        'color-info-soft': '232 222 252',
        'color-info-contrast': '68 40 140',
        'color-shadow': '52 24 68',
        'pm-gold': '210 90 190',
        'pm-felt': '100 48 160'
      },
      {
        'color-surface': '14 8 22',
        'color-surface-raised': '26 16 40',
        'color-surface-muted': '40 26 58',
        'color-surface-overlay': '32 20 48',
        'color-ink': '250 236 255',
        'color-ink-soft': '232 210 248',
        'color-ink-muted': '184 150 210',
        'color-ink-faint': '144 116 172',
        'color-ink-inverse': '14 8 22',
        'color-line': '54 36 82',
        'color-line-strong': '78 52 114',
        'color-border-card': '62 42 94',
        'color-accent': '236 120 220',
        'color-accent-soft': '68 28 84',
        'color-accent-contrast': '20 8 28',
        'color-info': '160 130 240',
        'color-info-soft': '40 28 78',
        'color-info-contrast': '220 210 255',
        'color-shadow': '0 0 0',
        'pm-gold': '236 120 220',
        'pm-felt': '110 48 170'
      }
    )
  },

  /** emerald / mint / white */
  emw: {
    id: 'emw',
    label: 'Emerald Mint White',
    ...pack(
      {
        'color-surface': '238 248 242',
        'color-surface-raised': '255 255 255',
        'color-surface-muted': '220 240 228',
        'color-surface-overlay': '246 252 248',
        'color-ink': '14 40 28',
        'color-ink-soft': '24 60 42',
        'color-ink-muted': '56 112 84',
        'color-ink-faint': '96 140 114',
        'color-ink-inverse': '255 255 255',
        'color-line': '198 228 210',
        'color-line-strong': '164 208 182',
        'color-border-card': '198 228 210',
        'color-accent': '16 150 88',
        'color-accent-soft': '208 244 224',
        'color-accent-contrast': '255 255 255',
        'color-info': '28 128 96',
        'color-info-soft': '214 240 228',
        'color-info-contrast': '14 88 62',
        'color-shadow': '16 48 32',
        'pm-gold': '48 184 112',
        'pm-felt': '16 110 68'
      },
      {
        'color-surface': '6 18 12',
        'color-surface-raised': '12 30 22',
        'color-surface-muted': '18 44 32',
        'color-surface-overlay': '14 36 26',
        'color-ink': '228 248 236',
        'color-ink-soft': '196 232 210',
        'color-ink-muted': '132 184 152',
        'color-ink-faint': '96 144 118',
        'color-ink-inverse': '6 18 12',
        'color-line': '28 56 40',
        'color-line-strong': '40 80 56',
        'color-border-card': '32 64 46',
        'color-accent': '64 214 130',
        'color-accent-soft': '14 56 36',
        'color-accent-contrast': '6 18 12',
        'color-info': '72 196 140',
        'color-info-soft': '12 48 34',
        'color-info-contrast': '186 244 214',
        'color-shadow': '0 0 0',
        'pm-gold': '64 214 130',
        'pm-felt': '20 100 64'
      }
    )
  },

  /** gold / champagne / black — luxury casino */
  gld: {
    id: 'gld',
    label: 'Gold Champagne Black',
    ...pack(
      {
        'color-surface': '248 244 236',
        'color-surface-raised': '255 252 246',
        'color-surface-muted': '236 226 208',
        'color-surface-overlay': '252 248 240',
        'color-ink': '36 28 14',
        'color-ink-soft': '58 46 22',
        'color-ink-muted': '118 96 52',
        'color-ink-faint': '148 126 82',
        'color-ink-inverse': '255 255 255',
        'color-line': '224 210 178',
        'color-line-strong': '204 184 142',
        'color-border-card': '224 210 178',
        'color-accent': '168 130 36',
        'color-accent-soft': '246 232 186',
        'color-accent-contrast': '255 255 255',
        'color-info': '140 112 40',
        'color-info-soft': '242 230 196',
        'color-info-contrast': '96 72 20',
        'color-shadow': '44 34 14',
        'pm-gold': '198 158 48',
        'pm-felt': '96 72 24'
      },
      {
        'color-surface': '10 8 4',
        'color-surface-raised': '22 18 10',
        'color-surface-muted': '36 30 16',
        'color-surface-overlay': '28 24 12',
        'color-ink': '250 240 214',
        'color-ink-soft': '232 216 176',
        'color-ink-muted': '186 164 110',
        'color-ink-faint': '148 130 88',
        'color-ink-inverse': '10 8 4',
        'color-line': '52 42 22',
        'color-line-strong': '78 64 32',
        'color-border-card': '60 50 26',
        'color-accent': '232 192 72',
        'color-accent-soft': '58 46 16',
        'color-accent-contrast': '14 10 4',
        'color-info': '210 178 80',
        'color-info-soft': '44 36 14',
        'color-info-contrast': '250 230 170',
        'color-shadow': '0 0 0',
        'pm-gold': '232 192 72',
        'pm-felt': '110 86 28'
      }
    )
  },

  /** midnight navy — deep blue brand surfaces */
  mdb: {
    id: 'mdb',
    label: 'Midnight Blue',
    ...pack(
      {
        'color-surface': '236 240 250',
        'color-surface-raised': '255 255 255',
        'color-surface-muted': '220 228 244',
        'color-surface-overlay': '244 246 252',
        'color-ink': '16 24 48',
        'color-ink-soft': '28 38 72',
        'color-ink-muted': '72 86 128',
        'color-ink-faint': '108 120 156',
        'color-ink-inverse': '255 255 255',
        'color-line': '200 210 232',
        'color-line-strong': '170 184 220',
        'color-border-card': '200 210 232',
        'color-accent': '52 72 168',
        'color-accent-soft': '220 226 250',
        'color-accent-contrast': '255 255 255',
        'color-info': '60 84 170',
        'color-info-soft': '222 230 252',
        'color-info-contrast': '36 52 120',
        'color-shadow': '24 32 64',
        'pm-gold': '96 120 210',
        'pm-felt': '40 56 130'
      },
      {
        'color-surface': '6 10 22',
        'color-surface-raised': '12 18 36',
        'color-surface-muted': '20 28 52',
        'color-surface-overlay': '16 22 44',
        'color-ink': '230 236 252',
        'color-ink-soft': '200 210 240',
        'color-ink-muted': '140 154 198',
        'color-ink-faint': '104 118 164',
        'color-ink-inverse': '6 10 22',
        'color-line': '32 42 74',
        'color-line-strong': '48 62 104',
        'color-border-card': '38 50 86',
        'color-accent': '120 148 255',
        'color-accent-soft': '28 36 80',
        'color-accent-contrast': '8 12 28',
        'color-info': '112 140 230',
        'color-info-soft': '22 30 68',
        'color-info-contrast': '198 210 255',
        'color-shadow': '0 0 0',
        'pm-gold': '120 148 255',
        'pm-felt': '48 64 140'
      }
    )
  }
}

export const THEME_IDS = Object.keys(THEMES)

export const isPalette = (value) => Boolean(value && THEMES[value])

/**
 * Parse host theme input into { palette, mode }.
 * - `pvw` → dark
 * - `pvw-light` → light
 * Palette alone always resolves to dark; light only via `-light` suffix.
 */
export function parseThemeInput (input) {
  if (input == null) return null

  if (typeof input === 'object') {
    return parseThemeInput(input.theme ?? input.palette ?? input.data ?? null)
  }

  const value = String(input).trim().toLowerCase()
  if (!value) return null

  const lightSuffix = value.match(/^([a-z0-9]+)[-_]light$/)
  if (lightSuffix && isPalette(lightSuffix[1])) {
    return { palette: lightSuffix[1], mode: 'light' }
  }

  // Optional explicit dark suffix for hosts that send palette-dark
  const darkSuffix = value.match(/^([a-z0-9]+)[-_]dark$/)
  if (darkSuffix && isPalette(darkSuffix[1])) {
    return { palette: darkSuffix[1], mode: 'dark' }
  }

  if (isPalette(value)) {
    return { palette: value, mode: 'dark' }
  }

  return null
}

export function resolveTokens (paletteId, mode) {
  const palette = THEMES[paletteId] || THEMES[DEFAULT_PALETTE]
  return palette[mode === 'light' ? 'light' : 'dark']
}

export function applyThemeToDocument (paletteId, mode) {
  if (typeof document === 'undefined') return

  const root = document.documentElement
  const tokens = resolveTokens(paletteId, mode)

  root.classList.toggle('dark', mode === 'dark')
  root.dataset.theme = mode
  root.dataset.palette = paletteId in THEMES ? paletteId : DEFAULT_PALETTE
  root.style.colorScheme = mode

  for (const [key, value] of Object.entries(tokens)) {
    root.style.setProperty(`--${key}`, value)
  }
}
